import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Plus, Trash2, Pencil, X, FileText, Link2, StickyNote, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  jobsAPI, JOB_TYPE_LABELS, JOB_STATUS_LABELS, JOB_STATUS_ORDER,
  JOB_STATUS_COLORS, JOB_PRIORITY_COLORS,
} from '../lib/jobsAPI';
import { documentsAPI } from '../lib/documentsAPI';
import { authAPI } from '../lib/api';
import { formatAUDate } from '../lib/dateUtils';

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Pipeline excludes 'archived' — it's a terminal off-pipeline state.
const PIPELINE = JOB_STATUS_ORDER.filter((s) => s !== 'archived');

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);

  // Documents & notes linked to this job
  const [jobDocs, setJobDocs] = useState([]);
  const [clientDocs, setClientDocs] = useState([]);
  const [linkDocOpen, setLinkDocOpen] = useState(false);
  const [jobNotes, setJobNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newNoteStaffOnly, setNewNoteStaffOnly] = useState(false);
  const [role, setRole] = useState(null);
  const [aiRunning, setAiRunning] = useState(false);

  const isClient = role === 'client';
  // An archived job's "Delete" permanently removes it (owner/admin only); on any
  // other job, "Delete" soft-archives as before.
  const isArchived = job?.status === 'archived';
  const canPurge = role === 'owner' || role === 'admin';

  const handleRunAIReview = async () => {
    setAiRunning(true);
    try {
      await jobsAPI.runAIReview(id);
      toast.success('AI review complete');
      loadJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI review failed');
    } finally {
      setAiRunning(false);
    }
  };

  useEffect(() => {
    loadRole();
    loadJob();
    loadJobDocs();
    loadJobNotes();
  }, [id]);

  const loadRole = async () => {
    try {
      const response = await authAPI.getMe();
      setRole(response.data.active_membership?.role);
    } catch (error) {
      // Default to non-client (staff) view if role is unknown.
    }
  };

  const loadJob = async () => {
    setLoading(true);
    try {
      const response = await jobsAPI.get(id);
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to load job');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadJobDocs = async () => {
    try {
      const response = await jobsAPI.listDocuments(id);
      setJobDocs(response.data);
    } catch (error) {
      // Non-fatal — job may load fine without docs
    }
  };

  const loadJobNotes = async () => {
    try {
      const response = await jobsAPI.listNotes(id);
      setJobNotes(response.data);
    } catch (error) {
      // Non-fatal
    }
  };

  const openLinkDoc = async () => {
    if (!job?.client_id) return;
    try {
      const response = await documentsAPI.list(job.client_id);
      // Only show client documents not already linked.
      const linkedIds = new Set(jobDocs.map((d) => d.id));
      setClientDocs(response.data.filter((d) => !linkedIds.has(d.id)));
      setLinkDocOpen(true);
    } catch (error) {
      toast.error('Failed to load client documents');
    }
  };

  const handleLinkDoc = async (documentId) => {
    try {
      await jobsAPI.linkDocument(id, documentId);
      toast.success('Document linked');
      setLinkDocOpen(false);
      loadJobDocs();
    } catch (error) {
      toast.error('Failed to link document');
    }
  };

  const handleUnlinkDoc = async (documentId) => {
    try {
      await jobsAPI.unlinkDocument(id, documentId);
      loadJobDocs();
    } catch (error) {
      toast.error('Failed to unlink document');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await jobsAPI.addNote(id, { body: newNote.trim(), is_staff_only: newNoteStaffOnly });
      setNewNote('');
      setNewNoteStaffOnly(false);
      loadJobNotes();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await jobsAPI.updateStatus(id, newStatus);
      setJob(response.data);
      toast.success(`Moved to "${JOB_STATUS_LABELS[newStatus]}"`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Status change not allowed');
    }
  };

  const handleAddItem = async () => {
    if (!newItem.trim()) return;
    try {
      const response = await jobsAPI.addChecklistItem(id, newItem.trim());
      setJob(response.data);
      setNewItem('');
    } catch (error) {
      toast.error('Failed to add checklist item');
    }
  };

  const handleToggleItem = async (itemId, completed) => {
    try {
      const response = await jobsAPI.updateChecklistItem(id, itemId, { completed });
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const response = await jobsAPI.deleteChecklistItem(id, itemId);
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const openEdit = () => {
    setEditData({
      title: job.title,
      priority: job.priority,
      due_at: job.due_at ? job.due_at.split('T')[0] : '',
      period_label: job.period_label || '',
      internal_notes: job.internal_notes || '',
      client_notes: job.client_notes || '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        title: editData.title,
        priority: editData.priority,
        period_label: editData.period_label || null,
        due_at: editData.due_at ? new Date(editData.due_at).toISOString() : null,
        internal_notes: editData.internal_notes || null,
        client_notes: editData.client_notes || null,
      };
      const response = await jobsAPI.update(id, payload);
      setJob(response.data);
      setEditOpen(false);
      toast.success('Job updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job');
    }
  };

  const handleDelete = async () => {
    try {
      await jobsAPI.delete(id);
      toast.success('Job deleted');
      navigate('/jobs');
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handlePurge = async () => {
    try {
      await jobsAPI.purge(id);
      toast.success('Job permanently deleted');
      navigate('/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete job');
    }
  };

  if (loading || !job) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const currentIdx = PIPELINE.indexOf(job.status);
  const checklist = job.checklist || [];
  const completedCount = checklist.filter((c) => c.completed).length;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/jobs')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Button>
        {!isClient && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openEdit} data-testid="edit-job-button">
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => (isArchived && canPurge ? setPurgeOpen(true) : setDeleteOpen(true))}
              data-testid="delete-job-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {isArchived && canPurge ? 'Delete Permanently' : 'Delete'}
            </Button>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="job-detail-title">
            {job.title}
          </h1>
          <Badge className={JOB_STATUS_COLORS[job.status]}>{JOB_STATUS_LABELS[job.status]}</Badge>
          <Badge className={JOB_PRIORITY_COLORS[job.priority]}>
            {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)}
          </Badge>
        </div>
        <p className="text-muted-foreground mt-1">
          {JOB_TYPE_LABELS[job.job_type]}
          {job.client_name ? ` · ${job.client_name}` : ''}
          {job.period_label ? ` · ${job.period_label}` : ''}
        </p>
      </div>

      {/* Status pipeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Workflow Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress segments */}
          <div className="flex gap-1">
            {PIPELINE.map((s, i) => (
              <div
                key={s}
                className={`h-2 flex-1 rounded-full ${i <= currentIdx ? 'bg-primary' : 'bg-muted'}`}
                title={JOB_STATUS_LABELS[s]}
              />
            ))}
          </div>

          {isClient ? (
            <div className="flex items-center gap-2">
              <Label className="text-sm">Current stage:</Label>
              <Badge className={JOB_STATUS_COLORS[job.status]}>{JOB_STATUS_LABELS[job.status]}</Badge>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-sm">Move to:</Label>
                <Select value={job.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-64" data-testid="job-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JOB_STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s}>{JOB_STATUS_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                The lifecycle enforces valid transitions (forward, one step back, archive, or re-open).
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Review panel — staff only */}
      {!isClient && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <span className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" /> AI Review</span>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="capitalize">{(job.ai_review_status || 'not_started').replace('_', ' ')}</Badge>
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={handleRunAIReview} disabled={aiRunning} data-testid="run-ai-review">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {aiRunning ? 'Reviewing...' : (job.ai_review ? 'Re-run Review' : 'Run AI Review')}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!job.ai_review ? (
              <p className="text-sm text-muted-foreground">
                No AI review yet. Run a review to get a summary, missing items, suggested documents and risk flags.
                The AI is advisory only — it never lodges or finalises anything.
              </p>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Confidence:</span>
                  <Badge className={
                    job.ai_review.result?.confidence === 'high' ? 'bg-green-100 text-green-700'
                    : job.ai_review.result?.confidence === 'medium' ? 'bg-amber-100 text-amber-700'
                    : 'bg-muted text-foreground/90'
                  }>
                    {job.ai_review.result?.confidence || 'low'}
                  </Badge>
                </div>
                {job.ai_review.result?.summary && (
                  <p className="text-foreground/90 whitespace-pre-wrap">{job.ai_review.result.summary}</p>
                )}
                <AIList title="Missing items" items={job.ai_review.result?.missing_items} />
                <AIList title="Suggested documents" items={job.ai_review.result?.suggested_documents} />
                <AIList title="Risks / flags" items={job.ai_review.result?.risks} />
                <AIList title="Assumptions" items={job.ai_review.result?.assumptions} />
                <AIList title="Needs human review" items={job.ai_review.result?.items_requiring_human_review} />
                <p className="text-xs text-muted-foreground/70 pt-2 border-t">
                  AI output is advisory — always confirm before acting. Reviewed {formatAUDate(job.ai_review.reviewed_at)}.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: details */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <DetailRow label="Client" value={job.client_name || '—'} />
            <DetailRow label="Job Type" value={JOB_TYPE_LABELS[job.job_type]} />
            <DetailRow label="Period" value={job.period_label || '—'} />
            <DetailRow label="Due Date" value={job.due_at ? formatAUDate(job.due_at) : '—'} />
            <div>
              <p className="text-muted-foreground mb-1">Assignee</p>
              {job.assignee_name ? (
                <div className="flex items-center gap-2">
                  <Avatar className="w-7 h-7">
                    <AvatarFallback className="bg-primary text-white text-xs">{getInitials(job.assignee_name)}</AvatarFallback>
                  </Avatar>
                  <span>{job.assignee_name}</span>
                </div>
              ) : <span className="text-muted-foreground/70">Unassigned</span>}
            </div>
            <div>
              <p className="text-muted-foreground mb-1">Reviewer</p>
              {job.reviewer_name ? (
                <div className="flex items-center gap-2">
                  <Avatar className="w-7 h-7">
                    <AvatarFallback className="bg-purple-600 text-white text-xs">{getInitials(job.reviewer_name)}</AvatarFallback>
                  </Avatar>
                  <span>{job.reviewer_name}</span>
                </div>
              ) : <span className="text-muted-foreground/70">No reviewer</span>}
            </div>
            <DetailRow label="AI Review" value={job.ai_review_status?.replace('_', ' ') || 'not started'} />
            <DetailRow label="Created" value={formatAUDate(job.created_at)} />
            {job.completed_at && <DetailRow label="Completed" value={formatAUDate(job.completed_at)} />}
          </CardContent>
        </Card>

        {/* Right: checklist + notes */}
        <div className="lg:col-span-2 space-y-6">
          {/* Checklist */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <span>Checklist</span>
                <Badge variant="outline">{completedCount}/{checklist.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {checklist.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No checklist items yet.</p>
              ) : (
                checklist.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 group" data-testid={`checklist-item-${item.id}`}>
                    <Checkbox
                      checked={item.completed}
                      disabled={isClient}
                      onCheckedChange={(checked) => !isClient && handleToggleItem(item.id, checked === true)}
                    />
                    <span className={`flex-1 text-sm ${item.completed ? 'line-through text-muted-foreground/70' : 'text-foreground/90'}`}>
                      {item.label}
                    </span>
                    {!isClient && (
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground/70 hover:text-red-500 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))
              )}
              {!isClient && (
                <div className="flex gap-2 pt-2">
                  <Input
                    placeholder="Add checklist item..."
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddItem(); } }}
                    data-testid="new-checklist-input"
                  />
                  <Button onClick={handleAddItem} className="bg-primary hover:bg-primary/90" size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes — clients never see internal notes */}
          <div className={`grid grid-cols-1 gap-6 ${isClient ? '' : 'md:grid-cols-2'}`}>
            {!isClient && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Internal Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap min-h-[60px]">
                    {job.internal_notes || <span className="text-muted-foreground/70">No internal notes</span>}
                  </p>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Client-Facing Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap min-h-[60px]">
                  {job.client_notes || <span className="text-muted-foreground/70">No client notes</span>}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Linked documents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <span className="flex items-center gap-2"><FileText className="w-5 h-5" /> Documents</span>
                {!isClient && (
                  <Button variant="outline" size="sm" onClick={openLinkDoc} data-testid="link-doc-button">
                    <Link2 className="w-4 h-4 mr-2" />
                    Link Document
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {jobDocs.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No documents linked to this job.</p>
              ) : (
                jobDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-2 border rounded-lg group" data-testid={`job-doc-${doc.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-muted-foreground/70 shrink-0" />
                      <span className="text-sm text-foreground/90 truncate">{doc.filename}</span>
                      <Badge variant="outline" className="text-[10px] shrink-0">{doc.visibility === 'client_visible' ? 'Client' : 'Staff'}</Badge>
                    </div>
                    {!isClient && (
                      <button
                        onClick={() => handleUnlinkDoc(doc.id)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground/70 hover:text-red-500 transition-opacity"
                        title="Unlink"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Job notes / activity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <StickyNote className="w-5 h-5" /> Job Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isClient && (
                <div className="space-y-2">
                  <Textarea
                    placeholder="Add a note about this job..."
                    rows={2}
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    data-testid="new-job-note-input"
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox id="note-staff-only" checked={newNoteStaffOnly} onCheckedChange={(c) => setNewNoteStaffOnly(c === true)} />
                      <Label htmlFor="note-staff-only" className="text-sm font-normal cursor-pointer">Staff only</Label>
                    </div>
                    <Button onClick={handleAddNote} className="bg-primary hover:bg-primary/90" size="sm">
                      <Plus className="w-4 h-4 mr-2" /> Add Note
                    </Button>
                  </div>
                </div>
              )}

              {jobNotes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No notes yet.</p>
              ) : (
                <div className="space-y-3">
                  {jobNotes.map((note) => (
                    <div key={note.id} className="p-3 border rounded-lg" data-testid={`job-note-${note.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground/90">{note.author_name}</span>
                        <div className="flex items-center gap-2">
                          {note.is_staff_only && <Badge variant="outline" className="text-[10px]">Staff only</Badge>}
                          <span className="text-xs text-muted-foreground/70">{formatAUDate(note.created_at)}</span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{note.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Link document dialog */}
      <Dialog open={linkDocOpen} onOpenChange={setLinkDocOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link a Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {clientDocs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No more client documents available to link.</p>
            ) : (
              clientDocs.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => handleLinkDoc(doc.id)}
                  className="w-full flex items-center gap-2 p-2 border rounded-lg hover:bg-muted/50 text-left"
                >
                  <FileText className="w-4 h-4 text-muted-foreground/70 shrink-0" />
                  <span className="text-sm text-foreground/90 truncate flex-1">{doc.filename}</span>
                  <Link2 className="w-4 h-4 text-primary shrink-0" />
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={editData.title || ''} onChange={(e) => setEditData({ ...editData, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={editData.priority} onValueChange={(v) => setEditData({ ...editData, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input type="date" value={editData.due_at || ''} onChange={(e) => setEditData({ ...editData, due_at: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Period Label</Label>
              <Input value={editData.period_label || ''} onChange={(e) => setEditData({ ...editData, period_label: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Internal Notes</Label>
              <Textarea rows={2} value={editData.internal_notes || ''} onChange={(e) => setEditData({ ...editData, internal_notes: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Client-Facing Notes</Label>
              <Textarea rows={2} value={editData.client_notes || ''} onChange={(e) => setEditData({ ...editData, client_notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={handleSaveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
            <AlertDialogDescription>
              This archives "{job.title}". It drops out of the active lists but can still be
              viewed and restored from the Archived filter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent delete confirm */}
      <AlertDialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this job?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{job.title}", its checklist, and all of its notes.
              Linked documents will be unlinked but not deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-700 hover:bg-red-800" onClick={handlePurge}>Delete Permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className="text-foreground">{value}</p>
    </div>
  );
}

function AIList({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-muted-foreground mb-1">{title}</p>
      <ul className="list-disc list-inside text-foreground/90 space-y-0.5">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
